import { supabase } from '../config/supabase';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_BACKEND_URL 
  : 'http://localhost:3001';

export const personalizedPlanService = {
  async generatePersonalizedPlan(quizAnswers) {
    try {
      console.log('🚀 Starting personalized plan generation...');
      console.log('📊 Quiz answers received:', quizAnswers);
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('❌ No active session found for personalized plan generation');
        throw new Error('No active session found');
      }

      console.log('✅ Session found, making API call for personalized plan...');
      console.log('� API URL:', `${API_BASE_URL}/api/personalized-plan/generate-plan`);

      const requestBody = { quizAnswers };
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${API_BASE_URL}/api/personalized-plan/generate-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        console.error('❌ API Error:', response.status, errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Received personalized plan from API:', data);
      
      if (!data.personalizedPlan) {
        console.error('❌ API returned success but no personalizedPlan field');
        throw new Error('Invalid API response structure');
      }
      
      return data.personalizedPlan;
    } catch (error) {
      console.error('❌ Error generating personalized plan:', error);
      console.error('❌ Error stack:', error.stack);
      
      // Enhanced fallback plan with more personalization based on quiz answers
      console.log('⚠️ Using enhanced fallback plan due to API error');
      
      // Analyze quiz answers for better fallback
      const quizText = quizAnswers.map(qa => 
        Array.isArray(qa.answer) ? qa.answer.join(', ') : qa.answer
      ).join(' ').toLowerCase();
      
      // Detect primary concerns and get specific user quote
      let primaryConcern = 'personal growth';
      let specificPhrase = 'your thoughtful responses';
      let userQuote = '';
      let personalizedInsight = '';
      let transformationVision = '';
      let destinyMessage = '';
      
      // Extract a compelling user quote for personalization
      if (quizAnswers.length > 0) {
        const meaningfulAnswer = quizAnswers.find(qa => {
          const answerText = Array.isArray(qa.answer) ? qa.answer.join(' ') : qa.answer;
          return answerText && answerText.length > 10; // Find substantial answer
        });
        
        if (meaningfulAnswer) {
          userQuote = Array.isArray(meaningfulAnswer.answer) ? 
            meaningfulAnswer.answer.join(', ') : meaningfulAnswer.answer;
          userQuote = userQuote.substring(0, 100) + (userQuote.length > 100 ? '...' : '');
        }
      }
      
      if (quizText.includes('anxiety') || quizText.includes('anxious') || quizText.includes('worry')) {
        primaryConcern = 'anxiety';
        personalizedInsight = userQuote ? 
          `When you shared "${userQuote}", I could feel the weight of constant worry you've been carrying. Your courage to reach out despite the anxiety shows incredible inner strength that most people never develop.` :
          `I can sense from your responses that anxiety has been like an unwelcome companion, creating doubt where there should be confidence. But your willingness to seek change tells me you have the warrior spirit needed for transformation.`;
        
        transformationVision = `I see you waking up each morning with a deep sense of calm in your chest - not because life is perfect, but because you've learned to trust yourself completely. The anxious thoughts that once controlled your days will become quiet whispers you barely notice. You'll move through challenges with grace, knowing that you have unshakeable inner peace.`;
        
        destinyMessage = `Your anxiety isn't your weakness - it's proof of how deeply you feel and care. This sensitivity, once channeled correctly, will become your greatest superpower. You're not broken; you're just a powerful soul learning to harness your gifts.`;
        
      } else if (quizText.includes('depression') || quizText.includes('sad') || quizText.includes('down') || quizText.includes('hopeless')) {
        primaryConcern = 'depression';
        personalizedInsight = userQuote ? 
          `Reading "${userQuote}", I can sense the heaviness you've been carrying - like you're living under a gray cloud while everyone else enjoys sunshine. Yet here you are, taking this brave step toward healing, which tells me your inner light is still burning.` :
          `I can feel the weight you've been carrying from your responses. Depression might have convinced you that you're broken, but I see someone with extraordinary depth and emotional intelligence who's just been overwhelmed by life's intensity.`;
        
        transformationVision = `I envision you rediscovering the joy that's been hiding beneath the surface. You'll wake up excited about the day ahead, feeling genuinely grateful for your life. The heavy fog will lift, revealing a world full of color and possibility that was always there, waiting for you to heal enough to see it again.`;
        
        destinyMessage = `Your depth of feeling isn't a curse - it's the sign of a soul capable of extraordinary joy and meaningful connection. The same heart that feels pain so deeply will experience healing, love, and happiness with incredible richness. Your breakthrough is coming.`;
        
      } else if (quizText.includes('relationship') || quizText.includes('partner') || quizText.includes('family') || quizText.includes('lonely')) {
        primaryConcern = 'relationships';
        personalizedInsight = userQuote ? 
          `From what you shared about "${userQuote}", I can see how much you value deep connection and how painful it's been when relationships feel strained or distant. Your desire for authentic relationships shows a beautiful heart that refuses to settle for surface-level connections.` :
          `I can tell from your responses that relationships are deeply important to you, and when they're not working, it affects everything. Your longing for genuine connection shows you have the capacity for the kind of love that most people only dream of.`;
        
        transformationVision = `I see you surrounded by relationships that truly nourish your soul - people who see and appreciate the real you. You'll feel completely confident in your ability to communicate your needs, set healthy boundaries, and attract people who celebrate rather than diminish your light. Love will flow naturally in all directions.`;
        
        destinyMessage = `Your heart's desire for deep connection isn't asking for too much - it's evidence that you're meant for extraordinary relationships. The loneliness you've felt is just preparation for appreciating the profound connections that are coming your way.`;
        
      } else if (quizText.includes('stress') || quizText.includes('overwhelm') || quizText.includes('burnout') || quizText.includes('pressure')) {
        primaryConcern = 'stress and overwhelm';
        personalizedInsight = userQuote ? 
          `When you mentioned "${userQuote}", I could feel how much you've been carrying on your shoulders. The pressure and overwhelm you're experiencing shows just how much you care about doing things right - but now it's time to care for yourself with that same dedication.` :
          `I can sense from your responses that you've been running on empty for too long, trying to meet everyone's expectations while neglecting your own needs. Your awareness of this pattern is the first step toward reclaiming your peace and energy.`;
        
        transformationVision = `I envision you moving through life with effortless grace and abundant energy. You'll have crystal-clear priorities, unshakeable boundaries, and a deep trust in your ability to handle whatever comes your way. Stress will transform from your enemy into your ally, motivating without overwhelming.`;
        
        destinyMessage = `Your high standards and caring nature are gifts, not burdens. You're learning to channel your drive in ways that energize rather than exhaust you. The peace and balance you seek isn't just possible - it's your natural state waiting to be reclaimed.`;
        
      } else {
        // General personal growth theme
        personalizedInsight = userQuote ? 
          `When you shared "${userQuote}", I could see someone who's ready to step into their full potential. Your self-awareness and honesty in these responses show the kind of emotional intelligence that creates lasting transformation.` :
          `From your thoughtful responses, I can see someone who's been on a journey of self-discovery and is ready for the next level of growth. Your willingness to look within shows the courage that most people never develop.`;
        
        transformationVision = `I see you stepping confidently into the person you were always meant to be - someone who trusts their intuition, loves themselves fully, and creates positive change wherever they go. You'll feel aligned with your purpose and excited about the impact you're making in the world.`;
        
        destinyMessage = `Your journey of growth isn't random - it's evidence that you're meant for something extraordinary. Every challenge you've faced has been preparing you for the breakthrough that's waiting on the other side of this work.`;
      }
      
      return {
        personalInsights: personalizedInsight,
        transformationVision,
        breakthroughTimeline: [
          `Week 1-2: Immediate relief as we address the ${primaryConcern} patterns that have been weighing you down most`,
          "Week 3-4: A major breakthrough happens - you'll notice yourself responding to triggers with newfound calm and clarity", 
          "Month 2: The new, healthier patterns become your natural way of being - no more forcing or struggling",
          "Month 3+: You'll look back amazed at how far you've come, and genuinely excited about who you're becoming"
        ],
        destinyMessage,
        ttsContent: `${personalizedInsight} ${transformationVision} This transformation isn't just possible for you - it's your inevitable next chapter. Week 1-2: Immediate relief. Week 3-4: Major breakthrough. Month 2: New patterns become natural. Month 3 and beyond: Complete transformation and excitement for your future.`
      };
    }
  },

  async generateTTS(text) {
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      const response = await fetch(`${API_BASE_URL}/api/personalized-plan/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate speech');
      }

      const audioBlob = await response.blob();
      return URL.createObjectURL(audioBlob);
    } catch (error) {
      console.error('Error generating TTS:', error);
      throw error;
    }
  }
};
